import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useGetProduct from '../../../hooks/products/useGetProduct';
import useEditProduct from '../../../hooks/products/useEditProduct';

const EditProduct = () => {
  const { id } = useParams();
  const { product, loading, error } = useGetProduct(id);
  const {
    updateProduct,
    deleteProductImage,
    uploadProductImage,
    updating,
    updateError,
  } = useEditProduct();

  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    sku: '',
    quantity: '',
  });
  const [imageUrl, setImageUrl] = useState('');
  const [newImageFile, setNewImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);


  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        sku: product.sku || '',
        quantity: product.quantity || '',
      });
      setImages(product.images || []);
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    setNewImageFile(e.target.files[0]);
  };

  const handleDeleteImage = async () => {
    if (window.confirm('Delete this image?')) {
      const success = await deleteProductImage(id, imageUrl);
      if (success) {
        setImageUrl('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Upload image first if selected
      if (newImageFile) {
        const newUrl = await uploadProductImage(id, newImageFile);
        setImageUrl(newUrl);
      }

      await updateProduct(id, { ...form });
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="text-center py-10 text-gray-500">Loading...</div>;
  if (error) return <div className="text-center py-10 text-red-500">Error: {error.message}</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 bg-white rounded-xl shadow-md mt-10">
      <h1 className="text-3xl font-bold text-center mb-8">Edit Product</h1>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Image Display */}
        <div className="flex flex-col gap-4">
          {imageUrl ? (
            <div className="relative border rounded overflow-hidden">
              <img src={imageUrl} alt="Product" className="w-full h-64 object-cover" />
              <button
                type="button"
                onClick={handleDeleteImage}
                className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
              >
                Delete Image
              </button>
            </div>
          ) : (
            <div className="h-64 bg-gray-100 flex items-center justify-center text-gray-400 rounded">
              No image available
            </div>
          )}
          {images.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Thumbnail</label>
            <div className="flex gap-2 overflow-x-auto">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`Thumbnail ${i + 1}`}
                  onClick={async () => {
                    if (img !== imageUrl) {
                      setImageUrl(img);
                      await updateProduct(id, { imageUrl: img });
                    }
                  }}
                  className={`w-24 h-24 object-cover rounded cursor-pointer border-2 ${
                    img === imageUrl ? 'border-blue-600' : 'border-transparent'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
          {/* Upload New Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Upload New Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 text-sm"
            />
          </div>
        </div>

        {/* Editable Fields */}
        <div className="space-y-4">
          {['name', 'description', 'price', 'sku', 'quantity'].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium capitalize">{field}</label>
              {field === 'description' ? (
                <textarea
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  rows={4}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <input
                  name={field}
                  type={field === 'price' || field === 'quantity' ? 'number' : 'text'}
                  value={form[field]}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded"
                />
              )}
            </div>
          ))}
        </div>

        <div className="col-span-full">
          <button
            type="submit"
            disabled={updating || uploading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
          >
            {updating || uploading ? 'Saving...' : 'Save Changes'}
          </button>
          {updateError && (
            <div className="text-red-500 text-center mt-2">{updateError.message}</div>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditProduct;
